import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "@base-ui/react/dialog";
import {
  BookOpen,
  Building2,
  Compass,
  GitCompare,
  Layers,
  Loader2,
  Newspaper,
  Package,
  Search,
  Server,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SEARCH_CATEGORIES,
  isLandingRoute,
  preloadSiteSearch,
  searchSite,
  type SearchCategory,
  type SearchResponse,
  type SearchResult,
} from "@/lib/siteSearch";

const CATEGORY_ICONS: Record<SearchCategory, LucideIcon> = {
  products: Layers,
  servers: Server,
  marketplace: Package,
  guides: Compass,
  docs: BookOpen,
  blog: Newspaper,
  compare: GitCompare,
  company: Building2,
};

const DEBOUNCE_MS = 150;
/** Per-category cap in the «همه» view; a category chip shows all of its hits. */
const GROUP_PREVIEW = 4;

function isTypingTarget(target: EventTarget | null) {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
}

type Filter = SearchCategory | "all";

type SiteSearchProps = {
  className?: string;
  /** "navbar": icon + label + shortcut hint; "icon": icon only (blog header). */
  variant?: "navbar" | "icon";
  /**
   * Client-side navigation for landing routes. Omitted outside the landing SPA
   * (the blog embed), where every result is a full page load.
   */
  navigateTo?: (url: string) => void;
};

/**
 * Site search: a trigger button plus a ⌘K / Ctrl+K / "/" command dialog that
 * searches landing pages, docs and blog together (see lib/siteSearch), grouped
 * and filterable by category. Also shipped standalone to the backend-rendered
 * blog via src/embed/site-search.tsx, so it avoids router hooks and sets the
 * resets it relies on explicitly (the embed has no Tailwind preflight).
 */
export function SiteSearch({ className, variant = "navbar", navigateTo }: SiteSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);
  // Prerendered as ⌘ and corrected after hydration so SSR and client agree.
  const [modKey, setModKey] = useState("⌘");

  useEffect(() => {
    if (!/Mac|iPhone|iPad/.test(navigator.platform)) setModKey("Ctrl ");
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "/" && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (open) preloadSiteSearch();
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    const id = ++requestId.current;
    if (!q) {
      setResponse(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = window.setTimeout(() => {
      searchSite(q).then((res) => {
        if (id !== requestId.current) return; // a newer keystroke won
        setResponse(res);
        setActive(0);
        setLoading(false);
      });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const counts = useMemo(() => {
    const byCategory = new Map<SearchCategory, number>();
    for (const r of response?.results ?? []) byCategory.set(r.category, (byCategory.get(r.category) ?? 0) + 1);
    return byCategory;
  }, [response]);

  // A filter left over from the previous query may now match nothing.
  const effectiveFilter: Filter = filter !== "all" && !counts.has(filter) ? "all" : filter;

  const groups = useMemo(() => {
    const results = response?.results ?? [];
    return SEARCH_CATEGORIES.filter((c) => effectiveFilter === "all" || c.key === effectiveFilter)
      .map((c) => {
        const items = results.filter((r) => r.category === c.key);
        return { ...c, items: effectiveFilter === "all" ? items.slice(0, GROUP_PREVIEW) : items, total: items.length };
      })
      .filter((g) => g.items.length > 0);
  }, [response, effectiveFilter]);

  // Flattened in display order so arrow keys walk group by group.
  const ordered = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const chooseFilter = (next: Filter) => {
    setFilter(next);
    setActive(0);
    listRef.current?.scrollTo({ top: 0 });
  };

  const go = (result: SearchResult) => {
    setOpen(false);
    setQuery("");
    if (navigateTo && isLandingRoute(result)) navigateTo(result.url);
    else window.location.assign(result.url);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (ordered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % ordered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + ordered.length) % ordered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const result = ordered[active];
      if (result) go(result);
    }
  };

  const q = query.trim();
  const chip = (selected: boolean) =>
    cn(
      "m-0 inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-solid px-3 py-1 font-sans text-[12px] transition-colors",
      selected
        ? "border-ink-950 bg-ink-950 text-white dark:border-white dark:bg-white dark:text-ink-950"
        : "border-black/[0.1] bg-transparent text-ink-600 hover:border-black/25 hover:text-ink-950 dark:border-white/15 dark:text-[#a1a1a6] dark:hover:text-white",
    );

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger
        aria-label="جست‌وجو در سایت"
        className={cn(
          variant === "navbar" &&
            "flex h-9 items-center gap-2 rounded-md text-ink-600 transition-colors hover:text-ink-950",
          className,
        )}
        onMouseEnter={preloadSiteSearch}
        onFocus={preloadSiteSearch}
      >
        <Search className="h-4 w-4" />
        {variant === "navbar" && (
          <>
            <span className="hidden text-[12px] lg:inline">جست‌وجو</span>
            <kbd className="hidden rounded border border-black/[0.1] px-1.5 py-px font-sans text-[10px] text-ink-500 lg:inline" dir="ltr">
              {modKey}K
            </kbd>
          </>
        )}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm transition-opacity data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <Dialog.Popup
          dir="rtl"
          initialFocus={() => document.getElementById("site-search-input")}
          className="fixed inset-x-4 top-[10vh] z-[61] mx-auto flex max-h-[75vh] max-w-[640px] flex-col overflow-hidden rounded-2xl bg-white font-sans text-[#1d1d1f] shadow-2xl outline-none transition-[opacity,transform] data-[ending-style]:scale-[0.98] data-[ending-style]:opacity-0 data-[starting-style]:scale-[0.98] data-[starting-style]:opacity-0 dark:bg-[#1c1c1e] dark:text-[#f5f5f7]"
        >
          <Dialog.Title className="sr-only">جست‌وجو در گردو</Dialog.Title>
          <div className="flex items-center gap-3 border-0 border-b border-solid border-black/[0.08] px-4 dark:border-white/10">
            {loading ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-ink-500" />
            ) : (
              <Search className="h-4 w-4 shrink-0 text-ink-500" />
            )}
            <input
              id="site-search-input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder="جست‌وجو در صفحات، مستندات و وبلاگ…"
              autoComplete="off"
              spellCheck={false}
              role="combobox"
              aria-expanded={ordered.length > 0}
              aria-controls="site-search-results"
              aria-activedescendant={ordered[active] ? `site-search-${active}` : undefined}
              className="m-0 h-14 w-full appearance-none border-0 bg-transparent p-0 font-sans text-[15px] text-inherit outline-none placeholder:text-ink-400 [&::-webkit-search-cancel-button]:hidden"
            />
            <Dialog.Close className="m-0 shrink-0 cursor-pointer rounded border border-solid border-black/[0.1] bg-transparent px-1.5 py-0.5 font-sans text-[10px] text-ink-500 hover:text-ink-900 dark:border-white/15">
              Esc
            </Dialog.Close>
          </div>

          {q && counts.size > 1 && (
            <div className="flex gap-1.5 overflow-x-auto border-0 border-b border-solid border-black/[0.08] px-4 py-2.5 [scrollbar-width:none] dark:border-white/10">
              <button type="button" onClick={() => chooseFilter("all")} className={chip(effectiveFilter === "all")}>
                همه
                <span className="opacity-60">{(response?.results.length ?? 0).toLocaleString("fa-IR")}</span>
              </button>
              {SEARCH_CATEGORIES.filter((c) => counts.has(c.key)).map((c) => (
                <button key={c.key} type="button" onClick={() => chooseFilter(c.key)} className={chip(effectiveFilter === c.key)}>
                  {c.label}
                  <span className="opacity-60">{(counts.get(c.key) ?? 0).toLocaleString("fa-IR")}</span>
                </button>
              ))}
            </div>
          )}

          <div ref={listRef} id="site-search-results" role="listbox" className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
            {!q && (
              <p className="m-0 px-3 py-8 text-center text-[13px] text-[#6e6e73] dark:text-[#a1a1a6]">
                در محصولات، بازارچه، راهنماها، مستندات و وبلاگ گردو جست‌وجو کنید.
              </p>
            )}

            {q && response && ordered.length === 0 && !loading && (
              <p className="m-0 px-3 py-8 text-center text-[13px] text-[#6e6e73] dark:text-[#a1a1a6]">
                نتیجه‌ای برای «{q}» پیدا نشد.
              </p>
            )}

            {q && response && !response.indexAvailable && (
              <p className="m-0 px-3 pb-2 pt-1 text-[11px] text-amber-700">
                جست‌وجوی صفحات و مستندات فقط در نسخه ساخته‌شده (build) در دسترس است.
              </p>
            )}

            {q &&
              groups.map((group) => {
                const Icon = CATEGORY_ICONS[group.key];
                const hidden = group.total - group.items.length;
                return (
                  <div key={group.key} role="group" aria-label={group.label} className="mb-1">
                    <p className="m-0 px-3 pb-1 pt-2 text-[11px] font-semibold text-[#6e6e73] dark:text-[#a1a1a6]">{group.label}</p>
                    {group.items.map((result) => {
                      const index = ordered.indexOf(result);
                      const selected = index === active;
                      return (
                        <a
                          key={result.id}
                          id={`site-search-${index}`}
                          data-index={index}
                          role="option"
                          aria-selected={selected}
                          href={result.url}
                          onMouseMove={() => setActive(index)}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                            e.preventDefault();
                            go(result);
                          }}
                          className={cn(
                            "flex gap-3 rounded-lg px-3 py-2.5 text-start text-inherit no-underline transition-colors",
                            selected && "bg-gerdoo-500/10",
                          )}
                        >
                          <Icon className={cn("mt-0.5 h-4 w-4 shrink-0 text-ink-400", selected && "text-gerdoo-700")} />
                          <span className="min-w-0">
                            <span className={cn("block truncate text-[13.5px] font-semibold", selected && "text-gerdoo-700")}>
                              {result.title}
                            </span>
                            {result.excerptHtml && (
                              <span
                                className="mt-0.5 line-clamp-2 block text-[12px] leading-5 text-[#6e6e73] dark:text-[#a1a1a6] [&_mark]:bg-transparent [&_mark]:font-semibold [&_mark]:text-gerdoo-700"
                                dangerouslySetInnerHTML={{ __html: result.excerptHtml }}
                              />
                            )}
                          </span>
                        </a>
                      );
                    })}
                    {hidden > 0 && (
                      <button
                        type="button"
                        onClick={() => chooseFilter(group.key)}
                        className="m-0 cursor-pointer border-0 bg-transparent px-3 py-1.5 font-sans text-[12px] text-gerdoo-700 hover:underline"
                      >
                        {hidden.toLocaleString("fa-IR")} نتیجه دیگر در {group.label}
                      </button>
                    )}
                  </div>
                );
              })}
          </div>

          <div className="hidden items-center gap-4 border-0 border-t border-solid border-black/[0.08] px-4 py-2 text-[11px] text-ink-500 sm:flex dark:border-white/10">
            <span><kbd className="font-sans">↑↓</kbd> جابه‌جایی</span>
            <span><kbd className="font-sans">Enter</kbd> باز کردن</span>
            <span><kbd className="font-sans">Esc</kbd> بستن</span>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
